import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const apiClient = axios.create({
  baseURL: API
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const adminLogin = async (email, password) => {
  const response = await axios.post(`${API}/auth/login`, { email, password });
  return response.data;
};

export const verifyAuth = async () => {
  const response = await apiClient.get('/auth/verify');
  return response.data;
};

export const logout = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminEmail');
};

// Testimonials
export const getTestimonials = async () => {
  const response = await axios.get(`${API}/testimonials`);
  return response.data;
};

export const getAllTestimonials = async () => {
  const response = await axios.get(`${API}/admin/testimonials`);
  return response.data;
};

export const createTestimonial = async (data) => {
  const response = await axios.post(`${API}/admin/testimonials`, data);
  return response.data;
};

export const updateTestimonial = async (id, data) => {
  const response = await axios.put(`${API}/admin/testimonials/${id}`, data);
  return response.data;
};

export const deleteTestimonial = async (id) => {
  const response = await axios.delete(`${API}/admin/testimonials/${id}`);
  return response.data;
};

// Image Upload
export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/upload/image`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Buyers
export const getBuyers = async () => {
  const response = await axios.get(`${API}/buyers`);
  return response.data;
};

// VC Partners
export const getVCPartners = async () => {
  const response = await axios.get(`${API}/vc-partners`);
  return response.data;
};

// Startups
export const getStartups = async () => {
  const response = await axios.get(`${API}/startups`);
  return response.data;
};

export const getAllStartups = async () => {
  const response = await axios.get(`${API}/admin/startups`);
  return response.data;
};

export const createStartup = async (data) => {
  const response = await axios.post(`${API}/admin/startups`, data);
  return response.data;
};

export const updateStartup = async (id, data) => {
  const response = await axios.put(`${API}/admin/startups/${id}`, data);
  return response.data;
};

export const deleteStartup = async (id) => {
  const response = await axios.delete(`${API}/admin/startups/${id}`);
  return response.data;
};

// Podcast
export const getPodcastEpisodes = async (limit = 20) => {
  const response = await axios.get(`${API}/podcast/episodes?limit=${limit}`);
  return response.data;
};

export const getFeaturedEpisodes = async () => {
  const response = await axios.get(`${API}/podcast/featured`);
  return response.data;
};

export const getAllFeaturedEpisodes = async () => {
  const response = await apiClient.get('/admin/featured-episodes');
  return response.data;
};

export const addFeaturedEpisode = async (data) => {
  const response = await apiClient.post('/admin/featured-episodes', data);
  return response.data;
};

export const updateFeaturedEpisode = async (id, data) => {
  const response = await apiClient.put(`/admin/featured-episodes/${id}`, data);
  return response.data;
};

export const removeFeaturedEpisode = async (id) => {
  const response = await apiClient.delete(`/admin/featured-episodes/${id}`);
  return response.data;
};

// YC Episodes
export const getAllYCEpisodes = async () => {
  const response = await apiClient.get('/admin/yc-episodes');
  return response.data;
};

export const addYCEpisode = async (data) => {
  const response = await apiClient.post('/admin/yc-episodes', data);
  return response.data;
};

export const removeYCEpisode = async (id) => {
  const response = await apiClient.delete(`/admin/yc-episodes/${id}`);
  return response.data;
};

// Top VC Episodes (Sequoia/a16z)
export const getAllTopVCEpisodes = async () => {
  const response = await apiClient.get('/admin/topvc-episodes');
  return response.data;
};

export const addTopVCEpisode = async (data) => {
  const response = await apiClient.post('/admin/topvc-episodes', data);
  return response.data;
};

export const removeTopVCEpisode = async (id) => {
  const response = await apiClient.delete(`/admin/topvc-episodes/${id}`);
  return response.data;
};

export const subscribeToPodcast = async (email) => {
  const response = await axios.post(`${API}/podcast/subscribe`, { email });
  return response.data;
};

// Buyer Applications
export const submitBuyerApplication = async (data) => {
  const response = await axios.post(`${API}/buyer-applications`, data);
  return response.data;
};

// VC Partner Applications
export const submitVCPartnerApplication = async (data) => {
  const response = await axios.post(`${API}/vc-partner-applications`, data);
  return response.data;
};

// Admin - Founder Applications
export const getAllApplications = async () => {
  const response = await apiClient.get('/admin/applications');
  return response.data;
};

export const updateApplication = async (id, data) => {
  const response = await apiClient.put(`/admin/applications/${id}`, data);
  return response.data;
};

export const deleteApplication = async (id) => {
  const response = await apiClient.delete(`/admin/applications/${id}`);
  return response.data;
};

// Admin - Buyer Applications
export const getAllBuyerApplications = async () => {
  const response = await apiClient.get('/admin/buyer-applications');
  return response.data;
};

export const updateBuyerApplication = async (id, data) => {
  const response = await apiClient.put(`/admin/buyer-applications/${id}`, data);
  return response.data;
};

export const deleteBuyerApplication = async (id) => {
  const response = await apiClient.delete(`/admin/buyer-applications/${id}`);
  return response.data;
};

// Admin - VC Partner Applications
export const getAllVCPartnerApplications = async () => {
  const response = await apiClient.get('/admin/vc-partner-applications');
  return response.data;
};

export const updateVCPartnerApplication = async (id, data) => {
  const response = await apiClient.put(`/admin/vc-partner-applications/${id}`, data);
  return response.data;
};

export const deleteVCPartnerApplication = async (id) => {
  const response = await apiClient.delete(`/admin/vc-partner-applications/${id}`);
  return response.data;
};

// Admin - Podcast Subscribers
export const getAllPodcastSubscribers = async () => {
  const response = await apiClient.get('/admin/podcast-subscribers');
  return response.data;
};

export const updatePodcastSubscriber = async (id, data) => {
  const response = await apiClient.put(`/admin/podcast-subscribers/${id}`, data);
  return response.data;
};

export const deletePodcastSubscriber = async (id) => {
  const response = await apiClient.delete(`/admin/podcast-subscribers/${id}`);
  return response.data;
};

// Resend to Airtable
export const resendToAirtable = async (id, formType) => {
  const response = await apiClient.post('/admin/resend-to-airtable', { id, form_type: formType });
  return response.data;
};

// Featured Testimonial
export const getFeaturedTestimonial = async () => {
  const response = await axios.get(`${API}/testimonials/featured`);
  return response.data;
};

// Sponsors
export const getSponsors = async () => {
  const response = await axios.get(`${API}/sponsors`);
  return response.data;
};

export const getAllSponsors = async () => {
  const response = await axios.get(`${API}/admin/sponsors`);
  return response.data;
};

export const createSponsor = async (data) => {
  const response = await axios.post(`${API}/admin/sponsors`, data);
  return response.data;
};

export const updateSponsor = async (id, data) => {
  const response = await axios.put(`${API}/admin/sponsors/${id}`, data);
  return response.data;
};

export const deleteSponsor = async (id) => {
  const response = await axios.delete(`${API}/admin/sponsors/${id}`);
  return response.data;
};

// Airtable Sync
export const syncFromAirtable = async () => {
  const response = await apiClient.post('/admin/airtable/sync');
  return response.data;
};

export const getAirtableRecords = async (skip = 0, limit = 100) => {
  const response = await apiClient.get(`/admin/airtable/records?skip=${skip}&limit=${limit}`);
  return response.data;
};

export const getAirtableSyncStatus = async () => {
  const response = await apiClient.get('/admin/airtable/sync-status');
  return response.data;
};

export const updateAirtableRecord = async (recordId, updates) => {
  const response = await apiClient.put(`/admin/airtable/records/${recordId}`, updates);
  return response.data;
};

export const clearLocalEdits = async (recordId) => {
  const response = await apiClient.delete(`/admin/airtable/records/${recordId}/local-edits`);
  return response.data;
};


// ============================================
// COMMUNITY PLATFORM API
// ============================================

// Events
export const getEvents = async (category = null, activeOnly = true) => {
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  params.append('active_only', activeOnly);
  const response = await axios.get(`${API}/events?${params}`);
  return response.data;
};

// Fast events endpoint - returns all data in one query
export const getEventsFast = async (category = null) => {
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  const response = await axios.get(`${API}/events/fast?${params}`);
  return response.data;
};

export const getEvent = async (eventId) => {
  const response = await axios.get(`${API}/events/${eventId}`);
  return response.data;
};

export const getEventCompanies = async (eventId) => {
  const response = await axios.get(`${API}/events/${eventId}/companies`);
  return response.data;
};

// Companies
export const getCompanies = async (eventId = null, category = null, skip = 0, limit = 50) => {
  const params = new URLSearchParams();
  if (eventId) params.append('event_id', eventId);
  if (category) params.append('category', category);
  params.append('skip', skip);
  params.append('limit', limit);
  const response = await axios.get(`${API}/companies?${params}`);
  return response.data;
};

export const getCompany = async (companyId) => {
  const response = await axios.get(`${API}/companies/${companyId}`);
  return response.data;
};

export const updateCompany = async (companyId, data) => {
  const response = await axios.put(`${API}/companies/${companyId}`, data, {
    withCredentials: true
  });
  return response.data;
};

// Profile Claiming
export const getClaimableCompanies = async () => {
  const response = await axios.get(`${API}/companies/claimable`, {
    withCredentials: true
  });
  return response.data;
};

export const claimCompany = async (companyId) => {
  const response = await axios.post(`${API}/companies/${companyId}/claim`, {}, {
    withCredentials: true
  });
  return response.data;
};

export const requestClaimWithVerification = async (companyId, email) => {
  const response = await axios.post(`${API}/companies/${companyId}/claim-request`, { email }, {
    withCredentials: true
  });
  return response.data;
};

export const verifyClaimCode = async (companyId, code) => {
  const response = await axios.post(`${API}/companies/${companyId}/verify-claim`, { code }, {
    withCredentials: true
  });
  return response.data;
};

// Upvotes
export const toggleUpvote = async (companyId) => {
  const response = await axios.post(`${API}/companies/${companyId}/upvote`, {}, {
    withCredentials: true
  });
  return response.data;
};

export const getUpvoteStatus = async (companyId) => {
  const response = await axios.get(`${API}/companies/${companyId}/upvote-status`, {
    withCredentials: true
  });
  return response.data;
};

// Batch upvote status - much faster for loading many companies
export const getBatchUpvoteStatus = async (companyIds) => {
  const response = await axios.post(`${API}/upvotes/batch-status`, 
    { company_ids: companyIds },
    { withCredentials: true }
  );
  return response.data;
};

// Comments
export const getComments = async (companyId) => {
  const response = await axios.get(`${API}/companies/${companyId}/comments`);
  return response.data;
};

export const addComment = async (companyId, body, parentId = null) => {
  const response = await axios.post(`${API}/companies/${companyId}/comments`, 
    { body, parent_id: parentId },
    { withCredentials: true }
  );
  return response.data;
};

export const deleteComment = async (commentId) => {
  const response = await axios.delete(`${API}/comments/${commentId}`, {
    withCredentials: true
  });
  return response.data;
};

// Admin - Import to Community Structure
export const importToCommunity = async () => {
  const response = await apiClient.post('/admin/import-to-community');
  return response.data;
};

// ============================================
// SLIDESHOW / PITCH DECK API
// ============================================

// Slideshows
export const getSlideshows = async () => {
  const response = await axios.get(`${API}/slideshows`);
  return response.data;
};

export const getSlideshow = async (id) => {
  const response = await axios.get(`${API}/slideshows/${id}`);
  return response.data;
};

export const createSlideshow = async (data) => {
  const response = await apiClient.post('/slideshows', data);
  return response.data;
};

export const updateSlideshow = async (id, data) => {
  const response = await apiClient.put(`/slideshows/${id}`, data);
  return response.data;
};

export const deleteSlideshow = async (id) => {
  const response = await apiClient.delete(`/slideshows/${id}`);
  return response.data;
};

// Slides
export const createSlide = async (slideshowId, data) => {
  const response = await apiClient.post(`/slideshows/${slideshowId}/slides`, data);
  return response.data;
};

export const getSlide = async (id) => {
  const response = await axios.get(`${API}/slides/${id}`);
  return response.data;
};

export const updateSlide = async (id, data) => {
  const response = await apiClient.put(`/slides/${id}`, data);
  return response.data;
};

export const deleteSlide = async (id) => {
  const response = await apiClient.delete(`/slides/${id}`);
  return response.data;
};

// Review slide actions
export const loadCompaniesToSlide = async (slideId, eventName = null) => {
  const params = new URLSearchParams({ source: 'companies' });
  if (eventName) params.append('event_name', eventName);
  const response = await apiClient.post(`/slides/${slideId}/load-companies?${params}`);
  return response.data;
};

export const toggleCompanyStar = async (slideId, companyId) => {
  const response = await apiClient.post(`/slides/${slideId}/companies/${companyId}/star`);
  return response.data;
};

export const setCompanyWinner = async (slideId, companyId, rank = 1) => {
  const response = await apiClient.post(`/slides/${slideId}/companies/${companyId}/winner?rank=${rank}`);
  return response.data;
};

export const updateSlideJudge = async (slideId, judgeId) => {
  const response = await apiClient.put(`/slides/${slideId}/judge?judge_id=${judgeId}`);
  return response.data;
};

export const updateSlideDecision = async (slideId, decisionId) => {
  const response = await apiClient.put(`/slides/${slideId}/decision?decision_id=${decisionId}`);
  return response.data;
};

// Slideshow dropdowns
export const getSlideshowJudges = async () => {
  const response = await axios.get(`${API}/slideshow/judges`);
  return response.data;
};

export const getSlideshowEvents = async () => {
  const response = await axios.get(`${API}/slideshow/events`);
  return response.data;
};

// ============================================
// DECISION MAKERS API (Homepage carousel)
// ============================================

export const getDecisionMakers = async () => {
  const response = await axios.get(`${API}/decision-makers`);
  return response.data;
};

export const getAllDecisionMakers = async () => {
  const response = await apiClient.get('/admin/decision-makers');
  return response.data;
};

export const createDecisionMaker = async (data) => {
  const response = await apiClient.post('/admin/decision-makers', data);
  return response.data;
};

export const updateDecisionMaker = async (id, data) => {
  const response = await apiClient.put(`/admin/decision-makers/${id}`, data);
  return response.data;
};

export const deleteDecisionMaker = async (id) => {
  const response = await apiClient.delete(`/admin/decision-makers/${id}`);
  return response.data;
};

// ============================================
// BUYER PITCH PODCAST API
// ============================================

export const getBuyerPitchSessions = async () => {
  const response = await axios.get(`${API}/buyer-pitches`);
  return response.data;
};

export const getBuyerPitchSession = async (slug) => {
  const response = await axios.get(`${API}/buyer-pitches/${slug}`);
  return response.data;
};

export const submitBuyerPitchApplication = async (sessionId, data) => {
  const response = await axios.post(`${API}/buyer-pitches/${sessionId}/apply`, data);
  return response.data;
};

export const createBuyerPitchCheckout = async (applicationId, packageType, originUrl) => {
  const response = await axios.post(`${API}/buyer-pitches/checkout`, null, {
    params: {
      application_id: applicationId,
      package: packageType,
      origin_url: originUrl
    }
  });
  return response.data;
};

export const getBuyerPitchPaymentStatus = async (checkoutSessionId) => {
  const response = await axios.get(`${API}/buyer-pitches/payment-status/${checkoutSessionId}`);
  return response.data;
};
